<template>
  <div v-if="isLoading" class="flex flex-col w-full gap-2 h-full justify-start min-h-[5rem] px-6 mb-6">
    <div class="flex w-full flex-col gap-2 border-b pb-4 mb-4">
      <div class="text-sm flex justify-between items-center ">
        <div class="flex flex-col gap-2 pe-4 ">
          <span class="text-xs text-gray-500">לקוח</span>
          <Skeleton borderRadius="0.5rem" width="5rem" height="1rem" />
        </div>
        <Skeleton borderRadius="0.5rem" width="10rem" height="2rem" />
      </div>
    </div>

    <div class="flex flex-col gap-4">
      <div class="flex flex-col w-full gap-2">
        <span class="text-xs text-gray-500">שירותים</span>
        <div class="flex gap-2" v-for="i of [1]" :key="i">
          <Skeleton borderRadius="0.75rem" width="5rem" height="5rem" class="flex-none" />
          <div class="flex flex-col w-full justify-start items-start gap-1">
            <Skeleton borderRadius="0.5rem" width="6rem" height="1rem" />
            <Skeleton borderRadius="0.5rem" width="8rem" height="1rem" />
            <Skeleton borderRadius="0.5rem" width="4rem" height="1rem" class="mt-auto" />
          </div>
        </div>
      </div>
      <div class="flex flex-col w-full gap-2">
        <span class="text-xs text-gray-500">מוצרים</span>
        <div class="flex gap-2" v-for="i of [1]" :key="i">
          <Skeleton borderRadius="0.75rem" width="5rem" height="5rem" class="flex-none" />
          <div class="flex flex-col w-full justify-start items-start gap-1">
            <Skeleton borderRadius="0.5rem" width="6rem" height="1rem" />
            <Skeleton borderRadius="0.5rem" width="8rem" height="1rem" />
            <Skeleton borderRadius="0.5rem" width="4rem" height="1rem" class="mt-auto" />
          </div>
        </div>
      </div>
    </div>

    <div class="w-full flex flex-col gap-4 border-t border-b py-4 my-4">
      <div class="w-full grid grid-cols-2 ">
        <div class="flex flex-col gap-2 pe-4">
          <span class="text-xs text-gray-500">שעת התחלה</span>
          <Skeleton borderRadius="0.5rem" width="6rem" height="1rem" />
        </div>
        <div class="flex flex-col gap-2 pe-4">
          <span class="text-xs text-gray-500">שעת סיום משוערת</span>
          <Skeleton borderRadius="0.5rem" width="6rem" height="1rem" />
        </div>
      </div>
    </div>
    <div class="flex justify-between items-center">
      <span>סה״כ לתשלום</span>
      <Skeleton borderRadius="0.5rem" width="4rem" height="1rem" />
    </div>
  </div>
  <div v-else class="flex flex-col w-full gap-2 h-full justify-start min-h-[5rem] overflow-y-scroll">
    <div v-if="isEvent" class="w-full flex flex-col px-6 gap-4">

      <div class="flex w-full flex-col gap-2 border-b pb-4">
        <DeliveryIssueInfo v-if="data.whatsappDeliveryIssue" :issue="data.whatsappDeliveryIssue" />

        <div class="text-sm flex justify-between items-center ">
          <div class="flex flex-col gap-2 pe-4 ">
            <span class="text-xs text-gray-500">לקוח</span>
            <span>{{ data.clientName }}</span>
          </div>
          <div class="flex gap-2 items-center">
            <span v-wave class="rounded-full px-4 py-1.5 border border-gray-300 flex-none" @click="goToClientPage">דף לקוח</span>
            <span v-wave class="rounded-full flex items-center justify-center p-2 border border-green-500 bg-green-50"
                  @click="sendMessage">
             <i class="pi pi-whatsapp text-2xl" />
            </span>
            <!-- Calling needs the actual number, so it goes away for restricted viewers -->
            <span v-if="canViewContact"
                  v-wave
                  class="rounded-full flex items-center justify-center p-2 border border-blue-500 bg-blue-50"
                  @click="openLinkNewTab(`tel:${data.clientPhone}`)">
             <i class="pi pi-phone text-2xl" />
            </span>
          </div>
        </div>
        <div v-if="data.customerBirthday" class="p-3 rounded-lg bg-primary-100 text-xs mt-2 gap-2 flex items-center">
          <i class="pi pi-gift text-primary-600 " />
          <span>ללקוח/ה יש יום הולדת החודש, בתאריך {{
              DateTime.fromISO(data.customerBirthday).toLocaleString(DateTime.DATE_SHORT)
            }}. תמסרו מזל טוב גם מאיתנו :) </span>
        </div>
        <div v-if="data.customerWarningNote"
             class="p-3 rounded-lg bg-red-50 border border-red-200 text-sm mt-2 gap-2 flex items-start">
          <i class="pi pi-exclamation-triangle text-red-600 mt-0.5" />
          <div class="flex flex-col gap-1">
            <span class="font-medium text-red-800">הערה חשובה</span>
            <span class="text-red-800 whitespace-pre-wrap">{{ data.customerWarningNote }}</span>
          </div>
        </div>
        <div v-if="data.customerInternalNote"
             class="p-3 rounded-lg bg-gray-100 text-sm mt-2 gap-2 flex items-start">
          <i class="pi pi-info-circle text-gray-600 mt-0.5" />
          <div class="flex flex-col gap-1">
            <span class="font-medium text-gray-700">הערה כללית</span>
            <span class="text-gray-700 whitespace-pre-wrap">{{ data.customerInternalNote }}</span>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div class="flex flex-col w-full gap-2">
          <span class="text-xs text-gray-500">שירותים</span>
          <service-item
            v-for="(service, idx) of services"
            :key="service.id"
            :selected-services="[]"
            mode="payment"
            size="small"
            :service="service" />
          <div v-if="currentStep" class="flex flex-col gap-2 mt-2">
            <span class="text-xs text-gray-500">שלב נוכחי</span>
            <div class="flex items-center text-sm gap-1">
              <span>{{ currentStep.description || `שלב ${currentStep.stepIndex + 1}` }}</span>
              <span class="text-gray-500 text-xs">({{ Math.round((currentStep.timeTo - currentStep.timeFrom) * 60) }} דקות)</span>
            </div>
          </div>
        </div>
        <div class="flex flex-col w-full gap-2" v-if="products.length">
          <span class="text-xs text-gray-500">מוצרים</span>
          <product-item
            v-for="(product, idx) of products?.map(({item}) => item)"
            :key="product.id"
            mode="payment"
            size="small"
            :product="product" />
        </div>
      </div>

      <div class="flex flex-col w-full gap-2 border-t pt-4">
        <span class="text-xs text-gray-500">משימות{{ taskProgress.total ? ` · ${taskProgress.done}/${taskProgress.total}` : '' }}</span>
        <AppointmentTasks
          :appointment-id="apid"
          :appointment-date="appointmentIsoDate"
          :customer-id="data.clientId"
          :customer-name="data.clientName"
          :can-add="canEdit"
          @progress="(done, total) => { taskProgress = { done, total }; }"
        />
      </div>

      <div class="w-full flex flex-col gap-4 border-t border-b py-4">
        <div class="w-full grid grid-cols-2">
          <div class="flex flex-col gap-2 pe-4">
            <span class="text-xs text-gray-500">שעת התחלה</span>
            <span class="text-sm">{{ appointmentSummary?.startTime }}</span>
          </div>
          <div class="flex flex-col gap-2 pe-4">
            <span class="text-xs text-gray-500">שעת סיום (משוער)</span>
            <span class="text-sm">{{ appointmentSummary?.endTime }}</span>
          </div>
        </div>
        <div v-if="adminStore.hasBranches" class="flex flex-col gap-2 pe-4 border-t pt-4">
          <span class="text-xs text-gray-500">סניף</span>
          <span class="text-sm">{{ branchName }}</span>
        </div>
        <span v-if="data.recurringManualEventId" class="text-xs text-gray-500">* זהו תור  חוזר</span>
        <!-- Approval status, spelled out — the calendar only shows it as a mark. -->
        <div
          v-if="statusInfo"
          class="p-3 rounded-lg font-medium text-sm gap-2 flex items-center"
          :class="statusInfo.boxClass">
          <span
            class="flex-none inline-flex items-center justify-center w-5 h-5 rounded-full"
            :class="statusInfo.iconWrapClass">
            <i class="pi" :class="statusInfo.icon" style="font-size: 0.7rem" />
          </span>
          {{ statusInfo.label }}
        </div>
        <div
          v-if="data.status === AppointmentStatus.NO_SHOW"
          class="p-3 rounded-lg bg-orange-50 font-medium text-sm gap-2 flex">
          <i class="pi pi-user-minus text-orange-600" style="font-size: 1.125rem" />
          הלקוח/ה לא הגיע/ה לתור
        </div>
        <CancellationInfo v-if="data.cancellation" :cancellation="data.cancellation" />
      </div>

      <div v-if="data.adminNote" class="w-full flex flex-col gap-4 border-b pb-4">
        <div class="flex flex-col gap-2 pe-4">
          <span class="text-xs text-gray-500">הערות</span>
          <span class="text-sm">{{ data.adminNote }}</span>
        </div>
      </div>

      <div v-if="data.paymentBreakdown" class="w-full flex flex-col gap-2">
        <div v-for="item in data.paymentBreakdown.items" :key="item.id"
             class="flex justify-between items-center text-sm">
          <span class="text-gray-500">{{ item.name }}</span>
          <span>{{ item.price >= 0 ? '₪' : '-₪' }}{{ Math.abs(item.price).toFixed(2) }}</span>
        </div>
        <div class="flex justify-between items-center">
          <span>{{ data.paymentStatus === 'success' ? 'סה״כ שולם' : 'סה״כ לתשלום' }}</span>
          <span>{{ data.paymentBreakdown.totalPrice.toFixed(2) }}</span>
        </div>
      </div>
      <span v-else-if="data.paymentDetails?.price">(סה״כ שולם ₪{{ data.paymentDetails.price }})</span>
      <div v-else-if="hasFromPriceService"
           class="flex justify-between items-center">
        <span>סה״כ לתשלום</span>
        <span>החל מ-₪{{ data.priceRange.minPrice }}</span>
      </div>
      <div v-else-if="data.priceRange.maxPrice !== data.priceRange.minPrice"
           class="flex justify-between items-center">
        <span>סה״כ לתשלום</span>
        <span> ₪{{ data.priceRange.maxPrice }} - ₪{{ data.priceRange.minPrice }}</span>
      </div>
      <div v-else class="flex justify-between items-center">
        <span>סה״כ לתשלום</span>
        <span> ₪{{ data.priceRange.minPrice }}</span>
      </div>
    </div>

    <!--   Actions   -->
    <div v-if="canEdit" class="w-full flex flex-col mt-4 md:mt-8">
      <span class="font-medium px-6 mb-4">פעולות</span>
      <select-container
        v-if="data.status === AppointmentStatus.WAITING_FOR_BUSINESS_APPROVAL && isEvent"
        title="אישור תור"
        subtitle="תור זה מחכה לאישור. לאחר אישור התור הודעה תישלח אל הלקוח"
        icon="pi-check-circle"
        :no-border="true"
        @click="approveAppointment"
      />
      <select-container
        v-if="!appointmentPassed && !appointmentIsDone && isEvent"
        title="עריכת הביקור"
        subtitle="עריכת שעות הביקור, אורך הביקור ושירותים"
        icon="pi-calendar-clock"
        :no-border="true"
        @click="openEditTimesDrawer"
      />
      <select-container
        v-if="!appointmentPassed && !appointmentIsDone && isManualEvent"
        title="עריכת האירוע"
        subtitle="עריכת שם האירוע ושעות ההתחלה והסיום"
        icon="pi-calendar-clock"
        :no-border="true"
        @click="openEditManualEventDrawer"
      />
      <select-container
        v-if="!appointmentPassed && !appointmentIsDone && isEvent && data.paymentStatus !== PaymentRequestStatus.Success"
        title="עריכת מוצרים"
        subtitle="הוסיפו או הסירו מוצרים לביקור"
        icon="pi-cart-arrow-down"
        :no-border="true"
        @click="openEditCartDrawer"
      />
      <select-container
        v-if="shouldShowSendPaymentLink && isEvent"
        title="תשלום"
        subtitle="בחרו את אופן התשלום"
        icon="pi-money-bill"
        :no-border="true"
        @click="openPaymentsDrawer"
      />
      <select-container
        v-if="shouldShowSendInvoiceLink && isEvent"
        title="שליחת קבלה"
        subtitle="שליחת המסמך בהודעה ללקוח"
        icon="pi-money-bill"
        :no-border="true"
        @click="sendInvoiceToCustomer"
      />
      <select-container
        v-if="canCompleteAppointment && isEvent"
        title="סיום תור"
        subtitle="סימון תור שהושלם"
        icon="pi-check-circle"
        :no-border="true"
        @click="completeAppointment"
      />
<!--      <select-container-->
<!--        v-if="canMarkNoShow && isEvent"-->
<!--        title="לא הגיע/ה"-->
<!--        subtitle="סימון שהלקוח/ה לא הגיע/ה לתור"-->
<!--        icon="pi-user-minus"-->
<!--        :no-border="true"-->
<!--        @click="markNoShow"-->
<!--        bg="orange"-->
<!--      />-->
      <select-container
        v-if="!appointmentIsDone"
        :title="cancelLabel"
        :subtitle="isEvent ? 'מחיקת תור מהיומן. הלקוח יקבל הודעת עדכון' : 'מחיקת האירוע מהיומן'"
        icon="pi-trash"
        :no-border="true"
        @click="cancelAppointment"
        bg="red"
      />
    </div>

    <!-- Recurring Event Action Modal -->
    <RecurringEventActionModal
      v-model:visible="showRecurringModal"
      action-type="delete"
      :event-title="data.title"
      @action="handleRecurringAction" />

    <!-- Cancellation Confirm Modal -->
    <CancelAppointmentConfirm
      v-model:visible="showCancelSmsChoice"
      :default-send-sms="adminAutomations.isCancellationAutomationEnabled"
      :automation-enabled="adminAutomations.isCancellationAutomationEnabled"
      @confirm="handleCancelConfirm" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useToast } from 'vue-toastification';
import { AppointmentsApi } from '@app/api/admin/appointments-api';
import { isFromPrice, openLinkNewTab, sendWhatsApp } from '@torim-app/utils';
import {
  AppointmentStatus,
  AppointmentType,
  IFullCalendarEvent,
  IService,
  IShopItem,
  MAIN_BRANCH_ID,
  PaymentProvider,
  PaymentRequestStatus,
  TAppointmentItem
} from '@torim-app/types';
import { AdminRouteNames } from '@app/router/route-names';
import { useAdminHomeStore, useAdminStore } from '@app/stores';
import { useNavigation } from '@app/composables/navigation';
import { PaymentsApi } from '@app/api/admin';
import { BundlePacksApi } from '@app/api/admin/bundle-packs-api';
import { PromotionsApi } from '@app/api/admin/promotions-api';
import { useConfirm } from 'primevue/useconfirm';
import EditCart from '@app/components/admin/modals/EditCart.vue';
import { DateTime } from 'luxon';
import RecurringEventActionModal, { RecurringActionScope } from './RecurringEventActionModal.vue';
import CancelAppointmentConfirm from './CancelAppointmentConfirm.vue';
import CancellationInfo from '@app/components/admin/components/CancellationInfo.vue';
import DeliveryIssueInfo from '@app/components/admin/components/DeliveryIssueInfo.vue';
import { RecurringEventsApi } from '@app/api/admin/recurring-events-api';
import { ProductItem, ServiceItem, SelectContainer, useDrawer } from '@torim-app/shared-components';
import EditAppointment from '@app/components/admin/modals/EditAppointment.vue';
import EditManualEvent from '@app/components/admin/modals/EditManualEvent.vue';
import SendCustomerMessage from '@app/components/admin/modals/SendCustomerMessage.vue';
import PaymentOptions from '@app/components/admin/modals/PaymentOptions.vue';
import { trackEvent } from '@app/composables/posthog';
import { useAdminAutomations } from '@app/stores/admin-automations';
import AppointmentTasks from '@app/components/admin/pages/tasks/AppointmentTasks.vue';

const emit = defineEmits(['close']);
const props = defineProps({
  apid: { type: String, required: true },
  date: { type: Date, required: true },
  currentStepIndex: { type: Number, required: false, default: undefined }
});

const adminStore = useAdminStore();
const adminHomeStore = useAdminHomeStore();
const adminAutomations = useAdminAutomations();
const canEdit = computed(() => adminStore.getCanEdit);
const canViewContact = computed(() => adminStore.getCanViewCustomerContact);
const taskProgress = ref({ done: 0, total: 0 });
const appointmentIsoDate = computed(() => DateTime.fromJSDate(props.date).setZone('Asia/Jerusalem').toISODate() ?? undefined);
const { goTo } = useNavigation();
const toast = useToast();
const confirm = useConfirm();
const { openDrawer, closeDrawer } = useDrawer();

const showRecurringModal = ref(false);
const showCancelSmsChoice = ref(false);
const cancelAppointmentLoading = ref(false);
const markCompletedLoading = ref(false);
const approveAppointmentLoading = ref(false);
const isLoading = ref(true);
const data = reactive<IFullCalendarEvent>({} as IFullCalendarEvent);

const isRecurringEvent = computed(() => !!data.recurringManualEventId);
const isEvent = computed(() => data.type === AppointmentType.EVENT);

const statusInfo = computed(() => {
  if (!isEvent.value) return null;
  switch (data.status) {
    case AppointmentStatus.WAITING_FOR_BUSINESS_APPROVAL:
      return { label: 'ממתין לאישורכם', icon: 'pi-clock', boxClass: 'bg-amber-50 text-amber-900', iconWrapClass: 'bg-amber-700 text-white' };
    case AppointmentStatus.APPROVED:
      return { label: 'הלקוח/ה אישר/ה הגעה', icon: 'pi-check', boxClass: 'bg-green-50 text-green-900', iconWrapClass: 'bg-green-700 text-white' };
    case AppointmentStatus.FINISHED:
      return { label: 'התור הושלם', icon: 'pi-check', boxClass: 'bg-green-50 text-green-900', iconWrapClass: 'bg-green-700 text-white' };
    case AppointmentStatus.VERIFIED:
      return { label: 'נקבע · הלקוח/ה עוד לא אישר/ה הגעה', icon: 'pi-calendar', boxClass: 'bg-gray-100 text-gray-800', iconWrapClass: 'bg-white text-gray-600 border border-gray-300' };
    default:
      return null;
  }
});
// A manual (owner-created) event — editable start/end times, no services/payment.
const isManualEvent = computed(() => data.type === AppointmentType.MANUAL);
const services = computed<IService[]>(() => adminStore.getServices.filter(x => data.servicesIds?.includes(x.id)) ?? []);
const hasFromPriceService = computed(() => services.value.some(s => isFromPrice(s)));
const currentStep = computed(() => {
  if (props.currentStepIndex == null || !data.steps?.length) return null;
  return data.steps.find(s => s.stepIndex === props.currentStepIndex) ?? null;
});

const products = computed<Array<TAppointmentItem & { item: IShopItem }>>(() => {
  if (!adminStore?.businessSiteData?.shop?.items) return [];

  return data.cartItems?.map<TAppointmentItem & { item: IShopItem }>(x => ({
    ...x,
    item: adminStore.businessSiteData.shop!.items.find(s => s.id === x.id)!
  }));
});

const branchName = computed<string>(() => {
  const branchId = data.branchId || MAIN_BRANCH_ID;
  return adminStore.getBranchSelectOptions.find(b => b.id === branchId)?.name ?? '';
});

const appointmentSummary = computed<{
  startTime: string;
  endTime: string;
}>(() => {
  const [startDate, startHour] = data.start?.split(' ');
  const [endDate, endHour] = data.end?.split(' ');
  return {
    startTime: `${DateTime.fromISO(startDate).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)} בשעה ${startHour}`,
    endTime: `${DateTime.fromISO(endDate).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)} בשעה ${endHour}`
  };
});

const hasVoucherSupport = ref(false);
const hasBundleSupport = ref(false);
const hasLoyaltyProgram = computed(() => !!adminStore?.businessSiteData?.loyaltyProgram);

const hasAnyPaymentOption = computed(() =>
  !!(adminStore?.businessSiteData?.hasPayments
    || adminStore?.businessSiteData?.hasInvoices
    || hasLoyaltyProgram.value
    || hasVoucherSupport.value
    || hasBundleSupport.value)
);

const shouldShowSendPaymentLink = computed(() => {
  // Show the payment button if the business supports any settlement method
  // (payment link, invoice, loyalty, vouchers, or bundle packs).
  return hasAnyPaymentOption.value &&
    data.paymentStatus !== PaymentRequestStatus.Success &&
    data.type !== AppointmentType.MANUAL &&
    data.type !== AppointmentType.OUT_OF_OFFICE;
});

const hasPayme = computed(() => {
  return adminStore?.businessSiteData?.paymentIntegration?.paymentConfig?.provider === PaymentProvider.PAYME;
});

const shouldShowSendInvoiceLink = computed(() => {
  return adminStore?.businessSiteData?.hasInvoices &&
    data.paymentStatus === PaymentRequestStatus.Success &&
    data.type !== AppointmentType.MANUAL &&
    data.type !== AppointmentType.OUT_OF_OFFICE
    && !hasPayme.value;
});

const appointmentPassed = computed(() => {
  const [date, hour] = data.start.split(' ');
  return DateTime.fromISO(date).endOf('day').diffNow('days').days < 0;
});

const appointmentIsDone = computed(() => data.status === AppointmentStatus.FINISHED || data.status === AppointmentStatus.NO_SHOW);

/**
 * True once the appointment's start time has passed.
 *
 * `data.start` is "yyyy-MM-dd HH:mm" in the business's own timezone, so it is parsed as a
 * local datetime rather than a bare date. Both actions below used to key off the day
 * *starting*, which meant a visit couldn't be completed or marked no-show until the
 * following day — long after the owner knows whether the customer turned up.
 */
const appointmentStartPassed = computed(() => {
  const start = DateTime.fromFormat(data.start, 'yyyy-MM-dd HH:mm');
  // A format we don't recognise shouldn't hide the actions outright; fall back to the
  // date alone so the previous (next-day) behaviour still applies.
  if (!start.isValid) return DateTime.fromISO(data.start.split(' ')[0]).endOf('day') < DateTime.now();
  return start < DateTime.now();
});

const canCompleteAppointment = computed(() =>
  appointmentStartPassed.value
  && !([AppointmentStatus.WAITING_FOR_BUSINESS_APPROVAL, AppointmentStatus.FINISHED, AppointmentStatus.NO_SHOW] as AppointmentStatus[]).includes(data.status)
);

const canMarkNoShow = computed(() =>
  appointmentStartPassed.value
  && !([AppointmentStatus.WAITING_FOR_BUSINESS_APPROVAL, AppointmentStatus.FINISHED, AppointmentStatus.NO_SHOW, AppointmentStatus.CANCELLED] as AppointmentStatus[]).includes(data.status)
);

const cancelLabel = computed(() => {
  if (data.type === AppointmentType.OUT_OF_OFFICE)
    return 'מחיקה';
  else if (data.type === AppointmentType.MANUAL)
    return 'מחיקת אירוע';
  return 'ביטול תור';
});

const openEditTimesDrawer = () => {
  trackEvent('appointment_edit_times_clicked');
  openDrawer(EditAppointment, {
    appointmentTitle: data?.title ?? '',
    appointmentDate: props.date,
    apid: data.id,
    recurringManualEventId: data.recurringManualEventId,
    steps: data.steps,
    servicesIds: data.servicesIds ?? [],
    onComplete: async () => {
      closeDrawer();
      await getAppointmentData();
      adminHomeStore.fetchBusinessCalendarDataSilently();
    },
    onClose: () => {
      closeDrawer();
    }
  }, {
    title: 'עריכת הביקור',
    full: true
  });
};

const openEditManualEventDrawer = () => {
  trackEvent('manual_event_edit_times_clicked');
  openDrawer(EditManualEvent, {
    apid: data.id,
    title: data.title,
    start: data.start,
    end: data.end,
    onComplete: async () => {
      closeDrawer();
      await getAppointmentData();
      adminHomeStore.fetchBusinessCalendarDataSilently();
    },
    onClose: () => {
      closeDrawer();
    }
  }, {
    title: 'עריכת האירוע'
  });
};

const openEditCartDrawer = () => {
  trackEvent('appointment_edit_cart_clicked');
  openDrawer(EditCart, {
    cartProducts: products.value,
    apid: data.id,
    onComplete: async () => {
      closeDrawer();
      await getAppointmentData();
    },
    onClose: () => {
      closeDrawer();
    }
  }, {
    title: 'עריכת מוצרים'
  });
};

const openPaymentsDrawer = () => {
  trackEvent('appointment_send_payment_clicked');
  openDrawer(PaymentOptions, {
    uuid: data.id,
    payFor: 'visit',
    event: data,
    hasVoucherSupport: hasVoucherSupport.value,
    hasBundleSupport: hasBundleSupport.value,
    onClose: (shouldLoad: boolean) => {
      closeDrawer();
      if (shouldLoad) {
        getAppointmentData();
        adminHomeStore.fetchBusinessCalendarDataSilently();
      }
    }
  }, {
    title: 'אפשרויות תשלום'
  });
};

const goToPage = (clientId: string, query?: Record<string, string>) => {
  goTo(AdminRouteNames.ClientProfile, { params: { id: clientId }, query });
};

const cancelAppointment = async () => {
  trackEvent('appointment_canceled');
  // If it's a recurring event, show recurring scope picker first
  if (isRecurringEvent.value) {
    showRecurringModal.value = true;
    return;
  }
  // Manual events / out-of-office blocks have no customer to notify, so skip the
  // "send cancellation message?" dialog and just delete.
  if (!isEvent.value) {
    await executeDeleteSingleAppointment(true);
    return;
  }
  showCancelSmsChoice.value = true;
};

const handleCancelConfirm = async (sendSms: boolean, reason?: string) => {
  await executeDeleteSingleAppointment(!sendSms, reason);
};

const handleRecurringAction = async (scope: RecurringActionScope) => {
  try {
    if (scope === 'instance') {
      // Manual/OOO events have no customer — delete directly; real appointments
      // defer to the "send cancellation message?" toggle.
      if (!isEvent.value) {
        await executeDeleteSingleAppointment(true);
      } else {
        showCancelSmsChoice.value = true;
      }
    } else {
      await executeDeleteEntireSeries();
    }
  } catch (e) {
    console.error(e);
    toast.error('הפעולה נכשלה, אנא נסו שוב');
  }
};

const executeDeleteSingleAppointment = async (skipNotification = false, reason?: string) => {
  cancelAppointmentLoading.value = true;
  try {
    const apid = data.id;
    if (!apid) {
      console.error('Could not find apid');
      return;
    }

    await AppointmentsApi.cancelAppointment({ apid, skipNotification, reason });

    toast.success('התור בוטל בהצלחה');
    emit('close', true);
    closeDrawer();
  } catch (e) {
    toast.error('לא הצלחנו לבטל את התור');
  } finally {
    cancelAppointmentLoading.value = false;
  }
};

const executeDeleteEntireSeries = async () => {
  cancelAppointmentLoading.value = true;
  try {
    const recurringId = data.recurringManualEventId;
    if (!recurringId) {
      console.error('Could not find recurringManualEventId');
      return;
    }

    await RecurringEventsApi.deleteEntireSeries(recurringId);
    toast.success('כל האירועים העתידיים בוטלו בהצלחה');
    emit('close', true);
    closeDrawer();
  } catch (e) {
    toast.error('לא הצלחנו לבטל את הסדרה');
  } finally {
    cancelAppointmentLoading.value = false;
  }
};

const approveAppointment = async () => {
  trackEvent('appointment_approved');
  confirm.require({
    message: 'האם לאשר את התור? הודעת אישור תישלח את הלקוח.',
    header: 'רק מוודאים',
    rejectLabel: 'לא',
    acceptLabel: 'כן',
    blockScroll: true,
    accept: async () => {
      approveAppointmentLoading.value = true;
      try {
        const apid = data.id;
        if (!apid) {
          console.error('Could not find apid');
          approveAppointmentLoading.value = false;
          return;
        }
        await AppointmentsApi.approveAppointment(apid);
        toast.success('התור אושר בהצלחה');
        emit('close', true);
        closeDrawer();
      } catch (e) {
        toast.error('לא הצלחנו לאשר את התור');
        approveAppointmentLoading.value = false;
      }
    }
  });
};

const completeAppointment = async () => {
  trackEvent('appointment_completed');
  confirm.require({
    message: `האם התור הסתיים? שימו ❤️ לא ניתן לבטל פעולה זו. ${adminStore.businessSiteData?.loyaltyProgram ? 'עבור מועדון לקוחות, כעת נחשב את צבירת הנקודות ללקוח/ה.' : ''}`,
    header: 'רק מוודאים',
    rejectLabel: 'לא',
    acceptLabel: 'כן',
    blockScroll: true,
    accept: async () => {
      markCompletedLoading.value = true;
      try {
        const apid = data.id as string;
        if (!apid) {
          console.error('Could not find apid');
          markCompletedLoading.value = false;
          return;
        }
        await AppointmentsApi.markAppointmentAsCompleted(apid);
        toast.success('עודכן בהצלחה');
        emit('close', true);
        closeDrawer();
      } catch (e) {
        toast.success('עודכן בהצלחה');
        markCompletedLoading.value = false;
      }
    }
  });
};

const markNoShow = async () => {
  trackEvent('appointment_no_show');
  confirm.require({
    message: 'האם לסמן שהלקוח/ה לא הגיע/ה?',
    header: 'רק מוודאים',
    rejectLabel: 'לא',
    acceptLabel: 'כן',
    blockScroll: true,
    accept: async () => {
      try {
        const apid = data.id as string;
        if (!apid) {
          console.error('Could not find apid');
          return;
        }
        await AppointmentsApi.markAppointmentAsNoShow(apid);
        toast.success('עודכן בהצלחה');
        emit('close', true);
        closeDrawer();
      } catch (e) {
        toast.error('לא הצלחנו לעדכן את הסטטוס');
      }
    }
  });
};

const sendMessage = () => {
  trackEvent('appointment_send_whatsapp_clicked');
  // Without the number there's nothing to hand WhatsApp, so restricted viewers compose
  // a message that the server delivers on their behalf.
  if (!canViewContact.value) {
    openDrawer(SendCustomerMessage, {
      customerId: data.clientId,
      customerName: data.clientName,
      onClose: closeDrawer
    }, {
      title: 'שליחת הודעה ללקוח/ה',
      closable: true
    });
    return;
  }

  try {
    sendWhatsApp(data.clientPhone);
  } catch (e) {
    console.error(e);
  }
};

const sendInvoiceToCustomer = async () => {
  trackEvent('appointment_send_invoice_clicked');
  try {
    await PaymentsApi.sendInvoiceLink(data.id);
    toast.success('החשבונית נשלחה בהצלחה :)');
    closeDrawer();
  } catch (e) {
    toast.error('לא הצלחנו לשלוח את החשבונית');
    console.log(e);
  }
};

const goToClientPage = () => {
  trackEvent('appointment_go_to_client_clicked');
  try {
    goToPage(data.clientId);
    emit('close');
    closeDrawer();
  } catch (e) {
    console.error(e);
  }
};

const getAppointmentData = async () => {
  try {
    isLoading.value = true;
    const res = await AppointmentsApi.fetchAppointmentData(props.apid);
    Object.assign(data, res);
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

const fetchPaymentSupportFlags = async () => {
  const [voucherTemplates, bundleTemplates, allVouchers] = await Promise.all([
    PromotionsApi.fetchVoucherTemplates().catch(() => []),
    BundlePacksApi.fetchTemplates().catch(() => []),
    PromotionsApi.fetchAllVouchers().catch(() => [])
  ]);
  hasVoucherSupport.value = voucherTemplates.length > 0 || allVouchers.length > 0;
  hasBundleSupport.value = bundleTemplates.length > 0;
};

onMounted(() => {
  getAppointmentData();
  fetchPaymentSupportFlags();
});
</script>

<style lang="scss">
.receipt-image {
  mask-image: linear-gradient(to top, rgba(0, 0, 0, 1.0) 50%, transparent 100%);
}
</style>
